import { useCallback, useEffect, useMemo, useState } from 'react';
import { toApiError } from '@/shared/api/errors';
import { listParticipants, moveParticipant } from '@/shared/api/paxes.api';
import { env } from '@/shared/config/env';
import { BULK_APPROVE, DEFAULT_GROUP_KEY, pastLabelFor } from '@/shared/config/event.config';
import { useEventConfig } from '@/shared/config/event-config-context';
import { pluralize } from '@/shared/lib/datetime';
import type { Participant } from '@/shared/types/pax';
import { useToast } from '@/shared/ui/toast/toast-context';

interface PageState {
  participants: Participant[];
  totalRecords: number;
  totalPages: number;
}

/** Resultado da última busca concluída, junto das chaves que a identificam. */
interface FetchResult {
  /** `group|audience|page` — muda quando o operador troca de filtro. */
  filterKey: string;
  /** `filterKey|reloadToken` — muda também em recarga manual. */
  requestKey: string;
  page: PageState | null;
  error: string | null;
}

const EMPTY_PAGE: PageState = { participants: [], totalRecords: 0, totalPages: 1 };
const INITIAL_RESULT: FetchResult = { filterKey: '', requestKey: '', page: null, error: null };

/**
 * Estado da tela de aprovações: filtros, paginação, carregamento e mutações.
 *
 * Decisões que valem registro:
 *
 * - **`loading` é derivado, não um `setState` dentro do efeito.** O efeito só
 *   escreve estado no callback assíncrono; o que a UI mostra vem da comparação
 *   entre a chave da busca em curso e a da última concluída. Isso elimina a
 *   cascata de renders que o React sinaliza em `setState` sincrono em efeito.
 * - **Skeleton só quando o filtro muda.** Recarga manual e refresh após uma
 *   decisão mantêm a tabela na tela (`refreshing`), em vez de piscar o skeleton.
 * - **`AbortController` por busca.** Trocar de aba rápido gerava respostas fora
 *   de ordem no painel antigo; aqui a requisição anterior é cancelada.
 * - **Atualização otimista.** Ao mover um participante, a linha sai na hora (ela
 *   não pertence mais ao filtro atual) e a página é recarregada em seguida para
 *   acertar os contadores. Em caso de erro, o recarregamento restaura.
 * - **Lote com `allSettled`.** "Aprovar todas" reporta quantas falharam, em vez
 *   de disparar requests soltos e ignorar o resultado.
 */
export function useApprovals() {
  const { notify } = useToast();
  const config = useEventConfig();

  // A aba inicial sai da parametrização: a declarada no documento local ainda vale,
  // desde que a API traga esse status; senão, é a primeira da ordem que a API definiu.
  const groupKeys = useMemo(() => config.groups.map(item => item.key), [config.groups]);
  const initialGroup = groupKeys.includes(DEFAULT_GROUP_KEY) ? DEFAULT_GROUP_KEY : (groupKeys[0] as string);

  const [group, setGroup] = useState<string>(initialGroup);
  // A trilha inicial é "todas" (`perfil` omitido), e não a primeira da parametrização:
  // o filtro por trilha é igualdade sobre o atributo de perfil no MICE, então abrir
  // filtrado escondia quem tem esse atributo em branco — em toda aba, sem nada na tela
  // indicando que faltava alguém. Trilha é refinamento; a lista inteira é o padrão.
  const [profile, setProfile] = useState<string>('');
  const [page, setPage] = useState(1);
  const [reloadToken, setReloadToken] = useState(0);

  const mapper = useMemo(() => ({ groupByMiceId: config.groupByMiceId }), [config.groupByMiceId]);
  const groupLabels = useMemo(() => new Map(config.groups.map(item => [item.key, item.label])), [config.groups]);

  const [result, setResult] = useState<FetchResult>(INITIAL_RESULT);
  const [pendingIds, setPendingIds] = useState<ReadonlySet<number>>(new Set());
  const [bulkRunning, setBulkRunning] = useState(false);

  const filterKey = `${group}|${profile}|${page}`;
  const requestKey = `${filterKey}|${reloadToken}`;

  useEffect(() => {
    const controller = new AbortController();

    listParticipants({ miceEventId: env.miceEventId, group, profile, page, pageSize: env.pageSize, mapper, signal: controller.signal })
      .then(fetched => {
        if (controller.signal.aborted) return;
        setResult({ filterKey, requestKey, page: fetched, error: null });
      })
      .catch((cause: unknown) => {
        if (controller.signal.aborted) return;
        const apiError = toApiError(cause);
        // 401 já derrubou a sessão no interceptor: a tela de login assume.
        if (apiError.isUnauthorized) return;
        setResult({ filterKey, requestKey, page: null, error: apiError.message });
      });

    return () => controller.abort();
  }, [group, profile, page, filterKey, requestKey, mapper]);

  const data = result.page ?? EMPTY_PAGE;
  /** Sem dados ainda para este filtro: a tabela não tem o que mostrar. */
  const loading = result.filterKey !== filterKey;
  /** Busca em curso sobre o mesmo filtro: mantém a tabela e sinaliza no botão. */
  const refreshing = !loading && result.requestKey !== requestKey;
  const error = loading ? null : result.error;

  const reload = useCallback(() => setReloadToken(token => token + 1), []);

  const changeGroup = useCallback((next: string) => {
    setGroup(next);
    setPage(1);
  }, []);

  const changeProfile = useCallback((next: string) => {
    setProfile(next);
    setPage(1);
  }, []);

  const goToPage = useCallback(
    (next: number) => {
      setPage(current => {
        const clamped = Math.max(1, Math.min(data.totalPages, next));
        return clamped === current ? current : clamped;
      });
    },
    [data.totalPages],
  );

  const markPending = useCallback((id: number, pending: boolean) => {
    setPendingIds(current => {
      const next = new Set(current);
      if (pending) next.add(id);
      else next.delete(id);
      return next;
    });
  }, []);

  /** Remove a linha da lista exibida sem esperar o recarregamento. */
  const dropFromView = useCallback((id: number) => {
    setResult(current => {
      if (!current.page) return current;
      return {
        ...current,
        page: {
          ...current.page,
          participants: current.page.participants.filter(item => item.id !== id),
          totalRecords: Math.max(0, current.page.totalRecords - 1),
        },
      };
    });
  }, []);

  /** Move um participante e avisa o resultado. */
  const move = useCallback(
    async (participant: Participant, target: string) => {
      markPending(participant.id, true);

      try {
        const { emailSent } = await moveParticipant({ miceEventId: env.miceEventId, paxId: participant.id, group: target });
        dropFromView(participant.id);
        // Rótulo em 3ª pessoa do documento local; sem ele, o nome do status que a
        // API trouxe já serve ("Inscrição de X aprovados." não é bonito, mas é
        // melhor do que mensagem sem o desfecho).
        const decision = `Inscrição de ${participant.nome} ${pastLabelFor(target, groupLabels.get(target) ?? target)}.`;
        // A decisão valeu de todo modo; o aviso é que não saiu. Dizer isso evita o
        // operador concluir que o participante foi notificado quando não foi.
        if (emailSent) {
          notify('success', decision);
        } else {
          notify('error', `${decision} O e-mail de aviso não foi enviado.`);
        }
        reload();
      } catch (cause) {
        const apiError = toApiError(cause);
        if (!apiError.isUnauthorized) {
          notify('error', `Não foi possível atualizar ${participant.nome}. ${apiError.message}`);
        }
      } finally {
        markPending(participant.id, false);
      }
    },
    [markPending, dropFromView, notify, reload, groupLabels],
  );

  /**
   * Aplica a decisão em lote na página atual.
   * O alcance é a página porque a API não expõe operação em lote — está
   * declarado como `scope: "page"` na parametrização.
   */
  const approveAllOnPage = useCallback(async () => {
    const targets = data.participants;
    if (targets.length === 0 || !BULK_APPROVE.enabled) return;

    setBulkRunning(true);

    const results = await Promise.allSettled(
      targets.map(participant =>
        moveParticipant({ miceEventId: env.miceEventId, paxId: participant.id, group: BULK_APPROVE.toGroup }),
      ),
    );
    const failed = results.filter(item => item.status === 'rejected').length;
    const succeeded = results.length - failed;
    // Aprovadas mas sem aviso: a decisão valeu e o participante não soube.
    const notNotified = results.filter(item => item.status === 'fulfilled' && !item.value.emailSent).length;

    setBulkRunning(false);

    if (succeeded > 0) {
      notify('success', `${pluralize(succeeded, 'inscrição aprovada', 'inscrições aprovadas')}.`);
    }
    if (notNotified > 0) {
      notify('error', `${pluralize(notNotified, 'inscrição aprovada sem e-mail de aviso', 'inscrições aprovadas sem e-mail de aviso')}.`);
    }
    if (failed > 0) {
      notify('error', `${pluralize(failed, 'inscrição não pôde ser aprovada', 'inscrições não puderam ser aprovadas')}.`);
    }

    reload();
  }, [data.participants, notify, reload]);

  /** Faixa exibida no rodapé ("1–100 de 254"). */
  const range = useMemo(() => {
    if (data.totalRecords === 0) return { from: 0, to: 0 };
    const from = (page - 1) * env.pageSize + 1;
    return { from, to: Math.min(data.totalRecords, from + data.participants.length - 1) };
  }, [page, data.totalRecords, data.participants.length]);

  return {
    group,
    profile,
    page,
    participants: data.participants,
    totalRecords: data.totalRecords,
    totalPages: data.totalPages,
    range,
    loading,
    refreshing,
    error,
    pendingIds,
    bulkRunning,
    changeGroup,
    changeProfile,
    goToPage,
    reload,
    move,
    approveAllOnPage,
  };
}
